import { z } from 'zod';
import { apiClient, apiGet, apiPost } from './client';
import { parseOrWarn } from '@/lib/zod-resolver';
import { paginationSchema } from './_types/common';
import type { Pagination } from './_types/common';

/**
 * 申报材料产物 API（v4 M3-ρ-4）— 接 backend M3-ζ 4 endpoint。
 *
 * 后端契约源：backend_app/app/api/v1/material_generation.py
 *   POST   /declaration-cases/{case_id}/artifacts      body: GenerateRequest
 *                                                      → { succeeded: [...], failed: [{artifact_type, error_message}] }
 *   GET    /declaration-cases/{case_id}/artifacts      → { items, total, page, page_size }
 *   GET    /artifacts/{artifact_id}/download           → FileResponse（二进制下载）
 *   DELETE /artifacts/{artifact_id}                    → { id }（PLATFORM only）
 *
 * RBAC（与 backend_app/app/core/auth.py 对位）：
 *   - artifact:generate / artifact:read — STAFF / TENANT_ADMIN / PLATFORM
 *   - artifact:delete                   — PLATFORM only
 *
 * 出参字段对位 GeneratedArtifactOut（schemas/generated_artifact.py:67-88）。
 * 关键安全约束：
 *   - 不含 file_path —— 后端隐藏物理路径（Sec Reviewer M3-ε M1 教训）
 *   - 全部 path param 走 encodeURIComponent（ρ-1 SR-med#2 教训）
 *   - 下载走应用层 download_url（不允许调用方拼 file_path）
 */

// === 产物类型白名单 ===
export const artifactTypeSchema = z.enum(['word', 'excel', 'pdf']);
export type ArtifactType = z.infer<typeof artifactTypeSchema>;

export const ARTIFACT_TYPE_LABEL: Record<ArtifactType, string> = {
  word: 'Word 申请书',
  excel: 'Excel 汇总表',
  pdf: 'PDF 整合本',
};

/** 文件扩展名（仅展示用 — 真实下载名由后端 Content-Disposition 给出） */
export const ARTIFACT_TYPE_EXT: Record<ArtifactType, string> = {
  word: 'docx',
  excel: 'xlsx',
  pdf: 'pdf',
};

// === GeneratedArtifactOut（不含 file_path） ===
export const generatedArtifactSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  declaration_case_id: z.string(),
  artifact_type: artifactTypeSchema,
  file_hash: z.string(),
  file_size: z.number().int().nullable(),
  template_pack_id: z.string(),
  generated_at: z.string(),
  generated_by_account_id: z.string().nullable(),
  created_at: z.string().nullable(),
  download_url: z.string().nullable(),
});
export type GeneratedArtifact = z.infer<typeof generatedArtifactSchema>;

export const artifactListSchema = paginationSchema(generatedArtifactSchema);

// === 部分成功响应（POST 生成） ===
export const generateFailureSchema = z.object({
  artifact_type: z.string(),
  error_message: z.string(),
});
export type GenerateFailure = z.infer<typeof generateFailureSchema>;

export const generateResponseSchema = z.object({
  succeeded: z.array(generatedArtifactSchema),
  failed: z.array(generateFailureSchema),
});
export type GenerateResponse = z.infer<typeof generateResponseSchema>;

// === 请求 body ===
export interface GenerateBody {
  /** 必填：要生成的产物类型清单（非空，1~3 个） */
  artifact_types: ArtifactType[];
  /** 可选：模板包目录名；为空则走 PolicyPack 默认（_default） */
  template_pack_id?: string;
}

// === encodeURIComponent path param（ρ-1 SR-med#2 教训） ===
function _safe(seg: string): string {
  return encodeURIComponent(seg);
}

/** 列出申报案下所有产物（按 generated_at desc） */
export async function listArtifacts(
  caseId: string,
): Promise<Pagination<GeneratedArtifact>> {
  const raw = await apiGet(`/declaration-cases/${_safe(caseId)}/artifacts`);
  return parseOrWarn(
    artifactListSchema,
    raw,
    `GET /declaration-cases/${caseId}/artifacts`,
  );
}

/**
 * 触发生成 —— 同步返回 {succeeded, failed}（部分成功是正常路径，不算 throw）。
 *
 * 后端守卫：
 *   - case.status == 'draft' → 409（草稿不可生成）
 *   - case 不存在            → 404
 *   - body.case_id != path   → 400（前端不暴露 case_id 给 body，下面自动注入）
 */
export async function generateArtifacts(
  caseId: string,
  body: GenerateBody,
): Promise<GenerateResponse> {
  // 后端 GenerateRequest 要求 body.case_id 与路径一致（防错配）；
  // 这里前端封装统一注入，调用方无需重复传。
  const payload: Record<string, unknown> = {
    case_id: caseId,
    artifact_types: body.artifact_types,
  };
  if (body.template_pack_id) {
    payload.template_pack_id = body.template_pack_id;
  }
  const raw = await apiPost(
    `/declaration-cases/${_safe(caseId)}/artifacts`,
    payload,
  );
  return parseOrWarn(
    generateResponseSchema,
    raw,
    `POST /declaration-cases/${caseId}/artifacts`,
  );
}

/**
 * 下载产物 — 浏览器原生触发（FileResponse + Content-Disposition）。
 *
 * 走 axios responseType:'blob' 而不是裸 window.open()：
 *   - window.open 拿不到 Authorization header，匿名请求会 401
 *   - blob 路径能复用 client.ts 拦截器（token + tenant header + 401 跳转）
 *
 * 注：返回 blob URL；调用方负责 a.click() + revokeObjectURL。
 */
export async function downloadArtifact(artifactId: string): Promise<{
  blob: Blob;
  filename: string;
}> {
  // _silent：拦截器不弹 toast，让 UI 层 mutation onError 走 truncateError 截断展示
  // （否则 4xx/5xx 时拦截器 + onError 双弹，且未截断长错误暴露栈痕）
  const res = await apiClient.get(`/artifacts/${_safe(artifactId)}/download`, {
    responseType: 'blob',
    _silent: true,
  });
  // 从 Content-Disposition 解 filename（后端 FileResponse 已设置）
  const disposition = String(res.headers?.['content-disposition'] ?? '');
  let filename = `artifact_${artifactId}`;
  const m =
    /filename\*=UTF-8''([^;]+)/i.exec(disposition) ||
    /filename="?([^";]+)"?/i.exec(disposition);
  if (m && m[1]) {
    try {
      filename = decodeURIComponent(m[1]);
    } catch {
      filename = m[1];
    }
  }
  return { blob: res.data as Blob, filename };
}

/**
 * 触发浏览器下载（Blob → a[download] → click → revoke）。
 *
 * 单独抽出方便 UI 层调用（不在 hook 内做副作用）。
 */
export async function triggerArtifactDownload(
  artifactId: string,
): Promise<void> {
  const { blob, filename } = await downloadArtifact(artifactId);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    // 异步 revoke 防止 Safari 在点击未触发前回收
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** 删除产物（PLATFORM only） */
export async function deleteArtifact(
  artifactId: string,
): Promise<{ id: string }> {
  // _silent：与 download 同理，由 UI mutation onError 走 truncateError 自定义提示
  const res = await apiClient.delete<{ id: string }>(
    `/artifacts/${_safe(artifactId)}`,
    { _silent: true },
  );
  return res.data;
}

// === UI 辅助 ===

/**
 * 产物类型 → 色彩 token 映射（0 hex — 全 CSS variable，ρ-2 教训）。
 * Word=蓝 / Excel=绿 / PDF=红，与品牌色谱对齐。
 */
export interface ArtifactTypeVisual {
  bg: string;
  fg: string;
  border: string;
}

export const ARTIFACT_TYPE_VISUAL: Record<ArtifactType, ArtifactTypeVisual> = {
  word: {
    bg: 'var(--color-primary-bg)',
    fg: 'var(--color-primary)',
    border: 'var(--color-primary-hover)',
  },
  excel: {
    bg: 'var(--color-success-bg)',
    fg: 'var(--color-success)',
    border: 'var(--color-success-2)',
  },
  pdf: {
    bg: 'var(--color-danger-bg-alt)',
    fg: 'var(--color-danger-2)',
    border: 'var(--color-danger-3)',
  },
};

/** 文件大小可读化（B / KB / MB） */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * 截断错误消息（防后端栈泄漏 / 信息过载，ρ-3 SR-med#2 教训）。
 * 默认 240 字符上限，超出加省略号。
 *
 * M3-ρ-4 Sec-med#1：剥离 POSIX/Windows 绝对路径片段（深度防御，避免后端短消息
 * 形如 `[Errno 2] '/opt/incubator/templates/...'` 把宿主目录结构泄漏给业务用户）。
 */
export function truncateError(msg: string, max = 240): string {
  if (!msg) return '（无详细信息）';
  const sanitized = msg
    .replace(/\/(?:opt|app|home|var|usr|tmp|etc)\/[^\s'"`<>]+/g, '<path>')
    .replace(/[A-Z]:\\[^\s'"`<>]+/g, '<path>');
  if (sanitized.length <= max) return sanitized;
  return `${sanitized.slice(0, max)}…`;
}

/** 案件状态 → 是否允许生成产物（前端 UX 过滤；真守卫在后端） */
export const ARTIFACT_GENERATABLE_STATUSES: ReadonlySet<string> = new Set([
  'frozen',
  'submitted',
  'reviewing',
  'approved',
  'rejected',
  'withdrawn',
]);

export function canGenerateArtifacts(status: string): boolean {
  return ARTIFACT_GENERATABLE_STATUSES.has(status);
}
